// src/App.js
import React, { useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import "./assets/styles/global.css";
import Questionaire from "./pages/Questionaire";
import AppAlert from "./components/appAlert/AppAlert";
import SignUpFarmer from "./pages/SignUpFarmer";
import NotFound from "./pages/NotFound";
import { refreshTokenIfExpired } from "./utils";
import MainLayout from './components/layout/MainLayout';
import ESGDetails from './pages/ESGDetails';
import Farmers from "./pages/Farmers";
import FarmerCard from "./components/FarmerCard";
import InclusiveSourcing from './pages/InclusiveSourcing';
import Organizations from "./pages/Organizations";
import { AppProvider } from './context/AppContext';
import AboutFarmer from "./pages/AboutFarmer";
import Summary from "./components/dashboard/Summary";
import ForgotPassword from "./pages/ForgotPassword";
// import PrivateRoute from './components/PrivateRoute';

// Protected Route Components
const PrivateRoute = ({ children, allowedRoles }) => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAuthenticated = !!user && (user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'farmer');
  const userRole = user?.farmer ? 'farmer' : user?.org ? 'admin' : 'guest';

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(userRole)) {
    return <Navigate to="/not-found" replace />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isSuperAdmin = user?.role === 'super_admin';
  // console.log("🚀 ~ AdminRoute ~ user:", user, user?.role === 'super_admin')

  if (user?.org) {
    return (
      <PrivateRoute allowedRoles={isSuperAdmin ? ['admin', 'superadmin'] : ['admin']}>
        <MainLayout>
          {/* Only render Organizations component for super_admin */}
          {window.location.pathname === '/organizations' && !isSuperAdmin ? (
            <Navigate to="/not-found" replace />
          ) : (
            children
          )}
        </MainLayout>
      </PrivateRoute>
    );
  }
  return <Navigate to="/login" replace />;
};

const FarmerRoute = ({ children }) => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  if (user?.farmer) {
    return (
      <PrivateRoute allowedRoles={['farmer']}>
        <MainLayout>{children}</MainLayout>
      </PrivateRoute>
    );
  }
  return <Navigate to="/login" replace />;
};

const DashboardRoute = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  if (user?.org) {
    // return <AdminRoute><Dashboard /></AdminRoute>;
    return <AdminRoute><InclusiveSourcing /></AdminRoute>;
  } else if (user?.farmer) {
    return <Navigate to="/farmer-dashboard" replace />;
  }
  return <Navigate to="/login" replace />;
};

const App = () => {

  useEffect(() => {
    refreshTokenIfExpired();
  }, []);

  return (
    <AppProvider>
      <Router>
        <AppAlert />
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/admin/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/register-with-invite/:token" element={<SignUpFarmer isFarmer={true} />} />
          <Route path="/register-with-invite-org/:token" element={<SignUpFarmer isFarmer={false} />} />

          {/* Admin Routes */}
          <Route path="/" element={
            <DashboardRoute>
            </DashboardRoute>
          }
          />
          <Route path="/esg-details" element={
            <AdminRoute>
              <ESGDetails />
            </AdminRoute>
          } />

          <Route path="/summary" element={
            <AdminRoute>
              <Summary />
            </AdminRoute>
          } />

          <Route path="/farmers" element={
            <AdminRoute>
              <Farmers />
            </AdminRoute>
          } />

          <Route path="/organizations" element={
            <AdminRoute>
              <Organizations />
            </AdminRoute>
          } />

          <Route path="/inclusive-sourcing" element={
            <AdminRoute>
              <InclusiveSourcing />
            </AdminRoute>
          } />

          {/* Farmer Routes */}
          <Route path="/questionnaire" element={
            <FarmerRoute>
              <Questionaire />
            </FarmerRoute>
          } />

          <Route path="/farmer-dashboard" element={
            <FarmerRoute>
              <FarmerCard />
            </FarmerRoute>
          } />

          <Route path="/about-farmer/:id" element={
            <MainLayout>
              <AboutFarmer />
            </MainLayout>
          } />

          <Route path="/about-farmer" element={
            <MainLayout>
              <AboutFarmer />
            </MainLayout>
          } />

          <Route path="/forgot-password" element={<ForgotPassword />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </AppProvider>
  );
};

export default App;
